<template>
  <div class="down">
    <div
      class="search-bar"
      :style="{
        margin: queryType || queryName ? '70px 60px 0px' : '70px 60px 70px',
      }"
    >
      <div>
        <el-button class="search-btn" @click="changeQueryType('')">
          全部游戏
        </el-button>
        <template v-for="item in Object.keys(GameQueryTypeEnum)" :key="item">
          <el-button class="search-btn" @click="changeQueryType(item)">
            {{ GameQueryTypeEnum[item] }}
          </el-button>
        </template>
      </div>
      <search-input v-model="name" @search="changeQueryName" />
    </div>
  </div>

  <template v-if="!queryName && !queryType">
    <div v-for="listType in Object.keys(GameListTypeEnum)" :key="listType">
      <div class="game-title">{{ GameListTypeEnum[listType] }}</div>
      <el-skeleton
        style="margin: 42px 60px 0"
        :loading="strategy[listType].loading"
        :rows="9"
        animated
      >
        <template #default>
          <el-scrollbar style="height: 100%" always>
            <div
              class="game-list"
              v-infinite-scroll="strategy[listType]?.fetchData"
              :infinite-scroll-disabled="!strategy[listType]?.hasMore"
              :infinite-scroll-distance="20"
            >
              <div
                v-for="game in strategy[listType]?.gameList"
                :key="game.id"
                class="game-item flex"
                @click="openDetail(game)"
              >
                <img
                  class="img"
                  :src="game.coverImage || game.listImage"
                  alt=""
                />
                <div class="info">
                  <div class="name-price flex flex-align-center">
                    <span class="name">{{ game.name }}</span>
                  </div>
                  <div class="types flex">
                    <div class="type-label">{{ game.introduction }}</div>
                  </div>
                  <span class="go" @click.stop="handleGo(game)">GO</span>
                </div>
              </div>
            </div>
          </el-scrollbar>
        </template>
      </el-skeleton>
    </div>
  </template>
  <template v-else>
    <el-skeleton
      style="margin: 42px 60px 0"
      :loading="searchLoading"
      :rows="9"
      animated
    >
      <template #default>
        <el-scrollbar style="height: 100%" always>
          <div
            class="game-list"
            v-infinite-scroll="fetchSearchData"
            :infinite-scroll-disabled="!hasMoreSearchData"
            :infinite-scroll-distance="20"
            :infinite-scroll-immediate="false"
          >
            <div
              v-for="game in searchGameList"
              :key="game.id"
              class="game-item flex"
              @click="openDetail(game)"
            >
              <img
                class="img"
                :src="game.coverImage || game.listImage"
                alt=""
              />
              <div class="info">
                <div class="name-price flex flex-align-center">
                  <span class="name">{{ game.name }}</span>
                </div>
                <div class="types flex">
                  <div class="type-label">{{ game.introduction }}</div>
                </div>
                <span class="go" @click.stop="handleGo(game)">GO</span>
              </div>
            </div>
          </div>
        </el-scrollbar>
      </template>
    </el-skeleton>
  </template>
</template>

<script setup>
import { ref } from "vue";
import { pageList } from "@/api/game";
import "swiper/css/pagination";
import "swiper/css";
import "swiper/css/navigation";
import SearchInput from "@/components/searchInput/SearchInput.vue";
import router from "@/router";

// 枚举
const GameQueryTypeEnum = { LATEST: "最新游戏", HOT: "热门游戏" };
const GameListTypeEnum = { CLASSIC: "经典小游戏", HOT: "热门小游戏" };
Object.freeze(GameQueryTypeEnum);
Object.freeze(GameListTypeEnum);

// region 筛选
const queryType = ref("");
const name = ref("");
const queryName = ref("");

const searchLoading = ref(false);
const searchData = { page: 1, size: 12 };
const searchGameList = ref([]);
const hasMoreSearchData = ref(true);

const refreshSearchData = () => {
  queryType.value = "";
  queryName.value = "";
  delete searchData.queryType;
  delete searchData.name;
  searchGameList.value = [];
  hasMoreSearchData.value = true;
  searchData.page = 1;
};

const changeQueryType = (type) => {
  refreshSearchData();
  name.value = "";

  // 发起搜索
  queryType.value = type;
  searchData.queryType = type;
  if (!type) {
    delete searchData.queryType;
    return;
  }
  fetchSearchData();
};

const changeQueryName = () => {
  refreshSearchData();

  // 发起搜索
  queryName.value = name.value;
  searchData.name = queryName.value;
  fetchSearchData();
};

const fetchSearchData = async () => {
  if (searchData.page === 1) {
    searchLoading.value = true;
  }
  try {
    const res = await pageList(null, searchData);
    if (res && res.code === 200) {
      searchGameList.value = searchGameList.value.concat(res.data.list);
      searchData.page++;
    }
    if (res.data.list.length === 0) {
      hasMoreSearchData.value = false;
    }
  } finally {
    searchLoading.value = false;
  }
};
// endregion

// region 默认列表
const strategy = ref({});
Object.keys(GameListTypeEnum).forEach((type) => {
  strategy.value[type] = {
    loading: false,
    gameList: [],
    hasMore: true,
    data: { page: 1, size: 12 },
    fetchData: async () => {
      if (strategy.value[type].data.page === 1) {
        strategy.value[type].loading = true;
      }
      try {
        const res = await pageList(
          { listType: type },
          strategy.value[type].data
        );
        if (res && res.code === 200) {
          const gameList = strategy.value[type].gameList;
          strategy.value[type].gameList = gameList.concat(res.data.list);
          strategy.value[type].data.page++;
          if (res.data.list.length === 0) {
            strategy.value[type].hasMore = false;
          }
        }
      } finally {
        strategy.value[type].loading = false;
      }
    },
  };
});
// endregion

// 方法
const openDetail = (game) => {
  router.push(`/_game/detail/${game.id}`);
};
const handleGo = (game) => {
  openDetail(game);
};
</script>

<style scoped lang="scss">
@import "@/style/rx";

.down {
  margin-bottom: 42px;

  .search-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .search-btn {
      width: 158px;
      height: 46px;
      border-radius: 10px;
      font-family: $font_YaHei;
      font-weight: 400;
      font-size: 20px;
      margin-right: 40px;
      font-style: normal;
      text-transform: none;
      background: var(--ux-glass-bg-subtle) !important;
      border: 0.5px solid var(--ux-glass-border) !important;
      color: var(--ux-text-primary) !important;
      backdrop-filter: var(--ios-glass-blur-light);
      -webkit-backdrop-filter: var(--ios-glass-blur-light);
      box-shadow: var(--ux-glass-shadow);
    }
  }
}

.game-title {
  font-family: $font_YaHei;
  font-weight: 400;
  font-size: 24px;
  color: var(--ux-text-primary);
  line-height: 28px;
  font-style: normal;
  text-transform: none;
  margin-left: 70px;
}

.game-list {
  margin-top: 42px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 0;
  justify-items: center;
  max-height: 420px;

  .game-item {
    padding: 3px;
    margin-bottom: 43px;
    width: 350px;
    height: 90px;
    background: var(--ux-card-bg);
    box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.12);
    border-radius: 24px;
    box-sizing: border-box;
    cursor: pointer;
    position: relative;

    &.list-item {
      transition: all 0.3s ease;
    }

    .go {
      position: absolute;
      top: 7px;
      right: 10px;
      width: 58px;
      height: 27px;
      line-height: 27px;
      border-radius: 34px;
      border: 1px solid var(--ux-line);
      opacity: 0.5;
      text-align: center;
      font-weight: 400;
      font-size: 14px;
      color: var(--ux-text-primary);
      font-style: normal;
      text-transform: none;
    }

    .img {
      margin-right: 18px;
      width: 85px;
      height: 85px;
      border-radius: 24px;
    }

    .name-price {
      margin-top: 5px;
      font-size: 14px;
      line-height: 30px;
      margin-bottom: 5px;
      white-space: nowrap;

      .name {
        margin-right: 18px;
        font-size: 16px;
        font-family: $font_Inter;
        color: #474343;
      }
    }

    .type-label {
      min-width: 70px;
      max-width: 320px;
      line-height: 22px;
      font-size: 12px;
      font-family: $font_Inter;
      color: #585858;
      display: inline-block;
      white-space: normal;
      word-break: break-all;
      word-wrap: break-word;
    }
  }
}
</style>
